import { useMemo } from 'react';
import { useLocation } from '@tanstack/react-router';
import { siteConfig } from '@/config/site';
import { DEFAULT_LANGUAGE } from '@/components/LanguageWrapper';

interface CanonicalOptions {
  /** Override the default path */
  path?: string;
  /** Include language prefix in canonical URL */
  includeLanguage?: boolean;
  /** Additional query params to include (rarely needed) */
  queryParams?: Record<string, string>;
}

/**
 * Normalize URL to prevent duplicate content issues
 * - Always uses https
 * - Never uses www
 * - Removes trailing slashes (except for root)
 * - Handles language prefixes consistently
 */
function normalizeUrl(baseUrl: string, path: string): string {
  // Ensure https
  let url = baseUrl.replace(/^http:\/\//, 'https://');
  
  // Remove www
  url = url.replace('://www.', '://');
  
  // Remove trailing slash from base URL
  url = url.replace(/\/$/, '');
  
  // Clean up path
  let cleanPath = path
    .replace(/\/+/g, '/') // Remove duplicate slashes
    .replace(/\/$/, ''); // Remove trailing slash
  
  // Ensure path starts with /
  if (cleanPath && !cleanPath.startsWith('/')) {
    cleanPath = '/' + cleanPath;
  }
  
  return url + cleanPath;
}

/**
 * Extract language from path
 * - /zh/about -> { lang: 'zh', pathWithoutLang: '/about' }
 * - /about -> { lang: 'en', pathWithoutLang: '/about' }
 */
function extractLanguageFromPath(path: string): { lang: string; pathWithoutLang: string } {
  const pathnameOnly = path.split('?')[0].split('#')[0];
  if (pathnameOnly.startsWith('/zh')) {
    const pathWithoutLang = pathnameOnly.replace(/^\/zh/, '') || '/';
    return { lang: 'zh', pathWithoutLang };
  }
  if (pathnameOnly.startsWith('/en')) {
    const pathWithoutLang = pathnameOnly.replace(/^\/en/, '') || '/';
    return { lang: 'en', pathWithoutLang };
  }
  // No prefix = English (default)
  return { lang: 'en', pathWithoutLang: pathnameOnly || '/' };
}

/**
 * Build canonical path based on language
 * - English: no prefix (e.g., /about)
 * - Chinese: /zh prefix (e.g., /zh/about)
 */
function buildCanonicalPath(pathWithoutLang: string, lang: string): string {
  if (lang === DEFAULT_LANGUAGE) {
    // English uses no prefix
    return pathWithoutLang === '/' ? '' : pathWithoutLang;
  }
  // Chinese uses /zh prefix
  return '/zh' + (pathWithoutLang === '/' ? '' : pathWithoutLang);
}

/**
 * Hook to generate and apply canonical URL
 */
export function useCanonicalUrl(options: CanonicalOptions = {}) {
  const location = useLocation();
  const { path: overridePath, includeLanguage = true, queryParams } = options;

  const canonicalUrl = useMemo(() => {
    const currentPath = overridePath || location.pathname;
    const { lang, pathWithoutLang } = extractLanguageFromPath(currentPath);
    
    // Determine the canonical path
    let canonicalPath: string;
    
    if (includeLanguage) {
      canonicalPath = buildCanonicalPath(pathWithoutLang, lang);
    } else {
      // Use path without language prefix (for truly language-agnostic pages)
      canonicalPath = pathWithoutLang;
    }
    
    // Build the full canonical URL
    let url = normalizeUrl(siteConfig.url, canonicalPath);
    
    // Add query params if specified (rarely needed for canonical URLs)
    if (queryParams && Object.keys(queryParams).length > 0) {
      const params = new URLSearchParams(queryParams);
      url += '?' + params.toString();
    }
    
    return url;
  }, [location.pathname, overridePath, includeLanguage, queryParams]);

  // Generate alternate language URLs for hreflang tags
  const alternateUrls = useMemo(() => {
    const { pathWithoutLang } = extractLanguageFromPath(overridePath || location.pathname);
    
    // English: no prefix, Chinese: /zh prefix
    const enPath = pathWithoutLang === '/' ? '' : pathWithoutLang;
    const zhPath = '/zh' + (pathWithoutLang === '/' ? '' : pathWithoutLang);
    
    return {
      en: normalizeUrl(siteConfig.url, enPath),
      zh: normalizeUrl(siteConfig.url, zhPath),
      'x-default': normalizeUrl(siteConfig.url, enPath), // Default to English
    };
  }, [location.pathname, overridePath]);

  return {
    canonicalUrl,
    alternateUrls,
  };
}

/**
 * Generate canonical URL for a given path (utility function)
 */
export function getCanonicalUrl(path: string, lang?: string): string {
  const { pathWithoutLang } = extractLanguageFromPath(path);
  const effectiveLang = lang || 'en';
  const canonicalPath = buildCanonicalPath(pathWithoutLang, effectiveLang);
  
  return normalizeUrl(siteConfig.url, canonicalPath);
}

export function getHreflangLinks(path: string): { en: string; zh: string; 'x-default': string } {
  const { pathWithoutLang } = extractLanguageFromPath(path);
  const enPath = pathWithoutLang === '/' ? '' : pathWithoutLang;
  const zhPath = '/zh' + (pathWithoutLang === '/' ? '' : pathWithoutLang);
  return {
    en: normalizeUrl(siteConfig.url, enPath),
    zh: normalizeUrl(siteConfig.url, zhPath),
    'x-default': normalizeUrl(siteConfig.url, enPath),
  };
}

export default useCanonicalUrl;
